import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { api, setAuthToken, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import { Camera, ArrowLeft, KeyRound, ScanLine } from "lucide-react";

const SCANNER_DIV_ID = "qr-login-scanner";

const QrLogin = () => {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const scannerRef = useRef(null);
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState("");
    const [manualToken, setManualToken] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const handledRef = useRef(false);

    const stopScanner = async () => {
        if (scannerRef.current) {
            try { await scannerRef.current.stop(); } catch (e) { /* ignore */ }
            try { await scannerRef.current.clear(); } catch (e) { /* ignore */ }
            scannerRef.current = null;
        }
    };

    useEffect(() => {
        return () => { stopScanner(); };
    }, []);

    // Auto-login from ?token= URL param (waiter scanned QR with phone camera and opened the URL)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get("token") || params.get("qr");
        if (fromUrl) {
            // small delay so React mounts loading UI
            setTimeout(() => tryLogin(fromUrl), 50);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tryLogin = async (token) => {
        if (handledRef.current) return;
        handledRef.current = true;
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/auth/qr-login", { token });
            if (data.token) setAuthToken(data.token);
            await stopScanner();
            setScanning(false);
            toast.success(`Benvenuto ${data.name}`, { duration: 2000 });
            await refresh();
            // Redirect by role
            if (data.role === "kitchen") navigate("/cucina", { replace: true });
            else if (data.role === "waiter") navigate("/mappa-sala", { replace: true });
            else if (data.role === "cashier") navigate("/cassa", { replace: true });
            else navigate("/dashboard", { replace: true });
        } catch (e) {
            setError(formatError(e));
            toast.error(formatError(e));
            handledRef.current = false;
        } finally {
            setSubmitting(false);
        }
    };

    const startScanner = async () => {
        setError("");
        handledRef.current = false;
        try {
            const html5Qr = new Html5Qrcode(SCANNER_DIV_ID, {
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                verbose: false,
            });
            scannerRef.current = html5Qr;
            setScanning(true);
            await html5Qr.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
                (decodedText) => {
                    // Accept full token or URL with ?qr=token / #qr=token
                    let token = decodedText.trim();
                    try {
                        const u = new URL(decodedText);
                        const fromQuery = u.searchParams.get("qr") || u.searchParams.get("token");
                        const hash = u.hash.startsWith("#") ? new URLSearchParams(u.hash.slice(1)) : null;
                        const fromHash = hash ? (hash.get("qr") || hash.get("token")) : null;
                        token = fromQuery || fromHash || token;
                    } catch (e) { /* not a url */ }
                    tryLogin(token);
                },
                () => {/* per-frame failure ignored */}
            );
        } catch (e) {
            setScanning(false);
            const msg = e?.message || String(e);
            setError(msg.includes("Permission") || msg.includes("denied")
                ? "Permesso fotocamera negato. Abilita la fotocamera o usa il codice manuale."
                : "Impossibile avviare la fotocamera: " + msg);
        }
    };

    const submitManual = (e) => {
        e.preventDefault();
        const t = manualToken.trim();
        if (!t) return;
        tryLogin(t);
    };

    return (
        <div className="min-h-screen bg-deep text-warm flex flex-col" data-testid="qr-login-page">
            <header className="px-4 lg:px-8 py-5 flex items-center justify-between border-b border-[#162033]">
                <Brand size="md" />
                <Link to="/login" className="text-sm text-elegant hover:text-brand flex items-center gap-1" data-testid="qr-login-back">
                    <ArrowLeft className="w-4 h-4" /> Login classico
                </Link>
            </header>

            <main className="flex-1 px-4 lg:px-8 py-8 flex items-start lg:items-center justify-center">
                <div className="w-full max-w-xl">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Accesso Rapido</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-bold mb-2">Scansiona il tuo QR personale</h1>
                    <p className="text-elegant text-sm mb-6">
                        Inquadra il QR fornito dall'amministratore per accedere immediatamente al terminale.
                    </p>

                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 lg:p-6 mb-4">
                        <div
                            id={SCANNER_DIV_ID}
                            className="w-full aspect-square max-w-md mx-auto bg-black/40 rounded-md overflow-hidden flex items-center justify-center text-elegant relative"
                            data-testid="qr-scanner-container"
                        >
                            {!scanning && (
                                <div className="text-center px-6">
                                    <ScanLine className="w-12 h-12 text-brand mx-auto mb-3" />
                                    <div className="text-sm">Premi "Avvia fotocamera" per iniziare</div>
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                            {!scanning ? (
                                <button onClick={startScanner} disabled={submitting}
                                    data-testid="qr-start-camera-button"
                                    className="flex-1 h-12 bg-brand text-deep font-bold rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
                                    <Camera className="w-4 h-4" /> Avvia fotocamera
                                </button>
                            ) : (
                                <button onClick={() => { stopScanner(); setScanning(false); }}
                                    data-testid="qr-stop-camera-button"
                                    className="flex-1 h-12 border border-[#1f2c44] rounded-md hover:border-brand">
                                    Interrompi scansione
                                </button>
                            )}
                        </div>

                        {error && <div className="mt-3 text-sm text-red-400" data-testid="qr-login-error">{error}</div>}
                    </div>

                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 lg:p-6">
                        <div className="text-xs uppercase tracking-[0.25em] text-elegant mb-3 flex items-center gap-2">
                            <KeyRound className="w-4 h-4" /> Codice manuale (fallback)
                        </div>
                        <form onSubmit={submitManual} className="flex gap-2">
                            <input
                                type="text"
                                value={manualToken}
                                onChange={(e) => setManualToken(e.target.value)}
                                placeholder="Incolla il token QR..."
                                data-testid="qr-manual-token-input"
                                className="flex-1 h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-xs"
                            />
                            <button type="submit" disabled={!manualToken.trim() || submitting}
                                data-testid="qr-manual-submit-button"
                                className="h-12 px-5 bg-brand text-deep font-bold rounded-md disabled:opacity-50">
                                Accedi
                            </button>
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default QrLogin;
